/** @fileoverview Connects Fabrik conversations to the AI SDK chat lifecycle. */

import { Chat as AIChat, useChat as useAIChat } from '@ai-sdk/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { completePendingQuestion, createConversation } from '../services/chatApi';
import { useChatStore } from '../stores/chatStore';
import type {
  AskQuestionAnswer,
  ChatAttachment,
  Conversation,
  FabrikUIMessage,
} from '../types/chat';
import type { Tenant } from '../types/identity';
import { selectionTitle, type PreviewSelectionItem } from '../lib/previewSelection';
import { refreshSavedMessages, toUIMessages } from './chatMessages';
import { createChatTransport } from './chatTransport';
import { DEFAULT_MAX_CHAT_RETRIES, useChatQueue } from './useChatQueue';
import type { AppCreatedCallback, AppDraftUpdatedCallback, ChatCallbacks } from './types';

/** Options for sending a message; everything is optional and defaults to the chat's own state. */
export type SendMessageOptions = {
  targetAppId?: string;
  onDraftUpdated?: AppDraftUpdatedCallback;
  onCreated?: AppCreatedCallback;
  /** Text to send instead of the composer's; may be empty when `selection` carries the request. */
  messageText?: string;
  retryConversationId?: string;
  initialPlanMode?: boolean;
  selection?: PreviewSelectionItem[];
  /** Files already stored for this conversation; a message may be files alone. */
  attachments?: ChatAttachment[];
  /** Called once the message is going out, before its reply streams. */
  onAccepted?: () => void;
};

/** Uses one persistent SDK chat per conversation; selection only changes the subscribed view. */
export function useChat(
  tenant: Tenant,
  appId?: string,
  onAppDraftUpdated?: AppDraftUpdatedCallback,
  onAppCreated?: AppCreatedCallback,
  maxChatRetries = DEFAULT_MAX_CHAT_RETRIES,
  planMode = false
) {
  const saved = useChatStore();
  const store = useChatStore.getState();
  const [query, setQuery] = useState('');
  const [conversationActivities, setConversationActivities] = useState<
    Map<string, 'streaming' | 'waiting-for-input' | 'completed-unvisited'>
  >(() => new Map());
  const [conversationPreviews, setConversationPreviews] = useState(new Map<string, string>());
  const [, setSelectedChat] = useState<AIChat<FabrikUIMessage> | undefined>();
  const chats = useRef(
    new Map<
      string,
      {
        chat: AIChat<FabrikUIMessage>;
        callbacks: ChatCallbacks;
        appId?: string;
        unsubscribeStatus?: () => void;
        unsubscribeMessages?: () => void;
      }
    >()
  );
  const selectionGeneration = useRef(0);
  const completedUnvisitedConversationIds = useRef(new Set<string>());
  const pendingStarts = useRef(new Set<number>());
  const failedConversationStart = useRef<
    | {
        text: string;
        conversationId: string;
        targetAppId?: string;
        planMode: boolean;
        onDraftUpdated?: AppDraftUpdatedCallback;
        onCreated?: AppCreatedCallback;
        selection?: PreviewSelectionItem[];
        attachments?: ChatAttachment[];
      }
    | undefined
  >(undefined);
  const lifecycleGeneration = useRef(0);
  const callbacks = useRef<ChatCallbacks>({ appId, onAppDraftUpdated, onAppCreated });
  const appIdRef = useRef(appId);
  const selectedId = useChatStore((state) => state.selected?.id);
  const getChat = useCallback(
    (conversationId: string) => chats.current.get(conversationId)?.chat,
    []
  );
  const tenantId = tenant.tenant_id;
  callbacks.current = { appId, onAppDraftUpdated, onAppCreated };
  appIdRef.current = appId;
  const refreshConversationActivities = useCallback(() => {
    const activities = new Map<string, 'streaming' | 'waiting-for-input' | 'completed-unvisited'>();
    const previews = new Map<string, string>();
    for (const [id, entry] of chats.current) {
      const { chat } = entry;
      const lastAssistantForPreview = [...chat.messages]
        .reverse()
        .find((message) => message.role === 'assistant');
      const responseText = lastAssistantForPreview?.parts
        .flatMap((part) => (part.type === 'text' ? [part.text.trim()] : []))
        .filter(Boolean)
        .join(' ');
      const question = lastAssistantForPreview?.parts.find(
        (part) => part.type === 'data-ask-question'
      );
      const preview =
        responseText || (question?.type === 'data-ask-question' ? question.data.question : '');
      if (preview) previews.set(id, preview);
      if (chat.status === 'submitted' || chat.status === 'streaming') {
        activities.set(id, 'streaming');
        continue;
      }
      const latestAssistantIndex = chat.messages.reduce(
        (latest, message, index) => (message.role === 'assistant' ? index : latest),
        -1
      );
      const latestUserIndex = chat.messages.reduce(
        (latest, message, index) => (message.role === 'user' ? index : latest),
        -1
      );
      const latestAssistant = chat.messages[latestAssistantIndex];
      if (
        latestAssistantIndex > latestUserIndex &&
        latestAssistant?.parts.some(
          (part) => part.type === 'data-ask-question' && part.data.phase === 'pending'
        )
      ) {
        activities.set(id, 'waiting-for-input');
      } else if (completedUnvisitedConversationIds.current.has(id)) {
        activities.set(id, 'completed-unvisited');
      }
    }
    setConversationActivities(activities);
    setConversationPreviews(previews);
  }, []);
  for (const entry of chats.current.values()) {
    if (entry.appId === appId) entry.callbacks.onAppDraftUpdated = onAppDraftUpdated;
  }

  const makeChat = useCallback(
    (
      conversation: Conversation,
      initial: FabrikUIMessage[] = [],
      turnCallbacks = callbacks.current
    ) => {
      const existing = chats.current.get(conversation.id);
      if (existing) {
        existing.callbacks = turnCallbacks;
        existing.appId = turnCallbacks.appId ?? conversation.app_id ?? appId;
        return existing.chat;
      }
      const entry: {
        chat: AIChat<FabrikUIMessage>;
        callbacks: ChatCallbacks;
        appId?: string;
        unsubscribeStatus?: () => void;
        unsubscribeMessages?: () => void;
      } = {
        chat: undefined as unknown as AIChat<FabrikUIMessage>,
        callbacks: turnCallbacks,
        appId: turnCallbacks.appId ?? conversation.app_id ?? appId,
      };
      const updatePersisted = async (
        chat: AIChat<FabrikUIMessage>,
        streamedMessages?: FabrikUIMessage[]
      ) => {
        if (useChatStore.getState().selected?.id !== conversation.id) return;
        try {
          await refreshSavedMessages(
            tenantId,
            conversation.id,
            (messages) => {
              if (chat.status !== 'submitted' && chat.status !== 'streaming') {
                chat.messages = messages;
              }
            },
            streamedMessages
          );
        } catch (error) {
          useChatStore.getState().handleError(error);
        } finally {
          if (useChatStore.getState().selected?.id === conversation.id) {
            useChatStore.setState({ loading: false });
          }
        }
      };
      const chat = new AIChat<FabrikUIMessage>({
        id: `fabrik:${tenantId}:${conversation.id}:${crypto.randomUUID()}`,
        messages: initial,
        transport: createChatTransport(tenantId, conversation.id),
        onError: (error) => {
          if (useChatStore.getState().selected?.id === conversation.id) {
            useChatStore.getState().handleError(error);
            updatePersisted(chat);
          }
        },
        onData: (part) => {
          if (
            part.type === 'data-app-draft-updated' &&
            part.data.appId === entry.appId &&
            part.data.appId === appIdRef.current
          ) {
            entry.callbacks.onAppDraftUpdated?.(part.data.definition);
          }
          const planUpdate = part as { type: string; data: { appId: string; revision: number } };
          if (planUpdate.type === 'data-app-plan-updated') {
            window.dispatchEvent(
              new CustomEvent('fabrik:app-plan-updated', { detail: planUpdate.data })
            );
          }
          if (part.type === 'data-app-created') {
            entry.appId = part.data.appId;
            entry.callbacks.onAppCreated?.(part.data.appId, {
              ...conversation,
              app_id: part.data.appId,
            });
          }
        },
        onFinish: ({ isAbort, isError, finishReason, messages }) => {
          if (
            finishReason === 'length' &&
            useChatStore.getState().selected?.id === conversation.id
          ) {
            useChatStore.setState({
              error: 'This reply reached the model limit. Ask Fabrik to continue.',
            });
          }
          if (!isAbort && !isError) {
            if (useChatStore.getState().selected?.id === conversation.id) {
              completedUnvisitedConversationIds.current.delete(conversation.id);
              useChatStore.setState({ loading: true });
            } else {
              completedUnvisitedConversationIds.current.add(conversation.id);
            }
            refreshConversationActivities();
            void updatePersisted(chat, messages);
            void useChatStore
              .getState()
              .loadConversations()
              .catch((error) => useChatStore.getState().handleError(error));
          }
        },
      });
      entry.chat = chat;
      chats.current.set(conversation.id, entry);
      entry.unsubscribeStatus = chat['~registerStatusCallback'](refreshConversationActivities);
      entry.unsubscribeMessages = chat['~registerMessagesCallback'](refreshConversationActivities);
      refreshConversationActivities();
      return chat;
    },
    [appId, refreshConversationActivities, tenantId]
  );
  const {
    queuedMessages,
    dispatchingMessage,
    isRunning: isChatRunning,
    submit: submitPrompt,
    runContinuation,
    removeQueuedMessage,
    steerQueuedMessage,
    clear: clearQueuedPrompts,
    clearAll: clearAllQueuedPrompts,
    retryAttempt,
    maxRetries,
    canRetry: canRetryQueuedPrompt,
    retryLast: retryQueuedPrompt,
  } = useChatQueue(
    makeChat,
    getChat,
    lifecycleGeneration,
    selectedId,
    setSelectedChat,
    maxChatRetries
  );

  const selectedConversation = useChatStore((state) => state.selected);
  const activeChat = selectedConversation
    ? chats.current.get(selectedConversation.id)?.chat
    : undefined;
  const placeholder = useRef<AIChat<FabrikUIMessage> | undefined>(undefined);
  if (!placeholder.current) {
    placeholder.current = new AIChat<FabrikUIMessage>({
      id: `fabrik:${tenantId}:placeholder`,
      transport: createChatTransport(tenantId),
    });
  }
  const active = useAIChat<FabrikUIMessage>({ chat: activeChat || placeholder.current });

  useEffect(() => {
    useChatStore.getState().setTenant(tenantId);
    const manager = chats.current;
    const completedUnvisitedIds = completedUnvisitedConversationIds.current;
    return () => {
      lifecycleGeneration.current += 1;
      failedConversationStart.current = undefined;
      for (const { chat, unsubscribeStatus, unsubscribeMessages } of manager.values()) {
        unsubscribeStatus?.();
        unsubscribeMessages?.();
        void chat.stop();
      }
      manager.clear();
      completedUnvisitedIds.clear();
      clearAllQueuedPrompts();
      setConversationActivities(new Map());
      setConversationPreviews(new Map());
      useChatStore.getState().clearTenant();
    };
  }, [clearAllQueuedPrompts, tenantId]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedChat(undefined);
      return;
    }
    const chat = chats.current.get(selectedId)?.chat;
    if (chat) setSelectedChat(chat);
  }, [selectedId]);

  const openConversation = useCallback(
    async (conversation: Conversation | null) => {
      failedConversationStart.current = undefined;
      selectionGeneration.current += 1;
      setQuery('');
      if (!conversation) {
        await useChatStore.getState().openConversation(null);
        setSelectedChat(undefined);
        return true;
      }
      const entry = chats.current.get(conversation.id);
      if (entry) {
        const opened = await useChatStore.getState().openConversation(conversation);
        if (opened) {
          if (completedUnvisitedConversationIds.current.delete(conversation.id)) {
            refreshConversationActivities();
          }
          if (entry.chat.status !== 'submitted' && entry.chat.status !== 'streaming') {
            entry.chat.messages = toUIMessages(useChatStore.getState().messages);
          }
          setSelectedChat(entry.chat);
        }
        return opened;
      }
      const opened = await useChatStore.getState().openConversation(conversation);
      if (opened) {
        if (completedUnvisitedConversationIds.current.delete(conversation.id)) {
          refreshConversationActivities();
        }
        const instance = makeChat(conversation, toUIMessages(useChatStore.getState().messages));
        setSelectedChat(instance);
      }
      return opened;
    },
    [makeChat, refreshConversationActivities]
  );

  const loadMoreMessages = useCallback(async () => {
    await useChatStore.getState().loadMoreMessages();
    const current = useChatStore.getState();
    const chat = current.selected && chats.current.get(current.selected.id)?.chat;
    if (chat) chat.messages = toUIMessages(current.messages);
  }, []);

  const sendMessage = useCallback(
    async (options: SendMessageOptions = {}) => {
      const {
        targetAppId = appId,
        onDraftUpdated = callbacks.current.onAppDraftUpdated,
        onCreated = callbacks.current.onAppCreated,
        messageText,
        retryConversationId,
        initialPlanMode = planMode,
        selection,
        attachments,
        onAccepted,
      } = options;
      const text = (messageText ?? query).trim();
      const state = useChatStore.getState();
      const selected = state.selected;
      let conversation = selected;
      if (
        (!text && !selection?.length && !attachments?.length) ||
        !state.tenantId ||
        state.planRequired ||
        state.quotaExhausted
      )
        return;
      failedConversationStart.current = undefined;
      const sendGeneration = selectionGeneration.current;
      let pendingNew = false;
      const lifecycleAtStart = lifecycleGeneration.current;
      if (!conversation) {
        if (pendingStarts.current.has(sendGeneration) || state.loading) return;
        pendingStarts.current.add(sendGeneration);
        pendingNew = true;
      } else {
        const alreadyRunning = isChatRunning(conversation.id, getChat(conversation.id));
        if (state.loading && !alreadyRunning) return;
      }
      const newConversationId = conversation
        ? undefined
        : retryConversationId ?? crypto.randomUUID();
      try {
        if (!conversation) {
          conversation = await createConversation(
            state.tenantId,
            text || selectionTitle(selection ?? []),
            undefined,
            targetAppId,
            newConversationId
          );
          if (lifecycleGeneration.current !== lifecycleAtStart) return;
          if (selectionGeneration.current === sendGeneration) {
            useChatStore.getState().setSelectedConversation(conversation);
          }
        }
        const turnCallbacks = {
          appId: targetAppId,
          onAppDraftUpdated: onDraftUpdated,
          onAppCreated: onCreated,
        };
        const prompt = {
          id: crypto.randomUUID(),
          text,
          model: state.model,
          appId: targetAppId,
          planMode: pendingNew && !targetAppId && initialPlanMode,
          callbacks: turnCallbacks,
          selection,
          attachments,
        };
        if (selectionGeneration.current === sendGeneration) setQuery('');
        // Everything that can refuse the message has passed; the caller may now release its inputs.
        onAccepted?.();
        await submitPrompt(conversation, prompt, lifecycleAtStart);
      } catch (error) {
        if (
          lifecycleGeneration.current === lifecycleAtStart &&
          (selectionGeneration.current === sendGeneration ||
            (conversation !== null && useChatStore.getState().selected?.id === conversation.id))
        ) {
          if (pendingNew && !conversation && newConversationId) {
            failedConversationStart.current = {
              text,
              conversationId: newConversationId,
              targetAppId,
              planMode: initialPlanMode && !targetAppId,
              onDraftUpdated,
              onCreated,
              selection,
              attachments,
            };
          }
          useChatStore.getState().handleError(error);
        }
      } finally {
        if (pendingNew) pendingStarts.current.delete(sendGeneration);
      }
    },
    [appId, getChat, isChatRunning, planMode, query, submitPrompt]
  );
  const canRetry = canRetryQueuedPrompt || Boolean(failedConversationStart.current);
  const retryLast = useCallback(() => {
    if (canRetryQueuedPrompt) {
      retryQueuedPrompt();
      return;
    }
    const failedStart = failedConversationStart.current;
    if (!failedStart) return;
    failedConversationStart.current = undefined;
    void sendMessage({
      targetAppId: failedStart.targetAppId,
      onDraftUpdated: failedStart.onDraftUpdated,
      onCreated: failedStart.onCreated,
      messageText: failedStart.text,
      retryConversationId: failedStart.conversationId,
      initialPlanMode: failedStart.planMode,
      selection: failedStart.selection,
      attachments: failedStart.attachments,
    });
  }, [canRetryQueuedPrompt, retryQueuedPrompt, sendMessage]);

  const answerQuestion = useCallback(
    async (answers: AskQuestionAnswer[]) => {
      const conversation = useChatStore.getState().selected;
      const chat = conversation && chats.current.get(conversation.id)?.chat;
      if (!conversation || !chat)
        throw new Error('Select a conversation before answering the question.');
      chat.clearError();
      useChatStore.getState().dismissError();
      const messages = chat.messages;
      const question = [...messages]
        .reverse()
        .find(
          (message) =>
            message.role === 'assistant' &&
            message.parts.some((part) => part.type === 'data-ask-question')
        );
      if (question)
        chat.messages = messages.map((message) =>
          message.id === question.id
            ? {
                ...message,
                parts: message.parts.map((part) =>
                  part.type === 'text' ? { type: 'reasoning', text: part.text } : part
                ),
              }
            : message
        );
      await runContinuation(conversation, () =>
        chat.sendMessage(undefined, { body: { answers, conversationId: conversation.id } })
      );
      if (chat.status === 'error') {
        throw chat.error ?? new Error('The assistant could not continue after your answer.');
      }
    },
    [runContinuation]
  );

  const stop = useCallback(() => {
    const { selected, tenantId: activeTenantId } = useChatStore.getState();
    if (!selected) return;
    clearQueuedPrompts(selected.id);
    const entry = chats.current.get(selected.id);
    if (!entry) return;
    const chat = entry.chat;
    void chat.stop().finally(async () => {
      const latestQuestion = [...chat.messages]
        .reverse()
        .find(
          (message) =>
            message.role === 'assistant' &&
            message.parts.some((part) => part.type === 'data-ask-question')
        );
      if (latestQuestion) {
        chat.messages = chat.messages.map((message) =>
          message.id === latestQuestion.id
            ? {
                ...message,
                parts: message.parts.map((part) =>
                  part.type === 'data-ask-question' && part.data.phase === 'pending'
                    ? { ...part, data: { ...part.data, phase: 'failed' } }
                    : part
                ),
              }
            : message
        );
      }
      try {
        await completePendingQuestion(activeTenantId!, selected.id);
        await refreshSavedMessages(activeTenantId!, selected.id, (messages) => {
          chat.messages = messages;
        });
      } catch {
        // A stopped stream may already have completed its question on the server.
      }
    });
  }, [clearQueuedPrompts]);

  const stopAll = useCallback(() => {
    lifecycleGeneration.current += 1;
    failedConversationStart.current = undefined;
    for (const { chat, unsubscribeStatus, unsubscribeMessages } of chats.current.values()) {
      unsubscribeStatus?.();
      unsubscribeMessages?.();
      void chat.stop();
    }
    chats.current.clear();
    clearAllQueuedPrompts();
    completedUnvisitedConversationIds.current.clear();
    setConversationActivities(new Map());
    setConversationPreviews(new Map());
    setSelectedChat(undefined);
  }, [clearAllQueuedPrompts]);
  const refreshSelectedMessages = useCallback(async () => {
    const { selected, tenantId: activeTenantId } = useChatStore.getState();
    if (!selected || !activeTenantId) return;
    const chat = chats.current.get(selected.id)?.chat;
    try {
      await refreshSavedMessages(
        activeTenantId,
        selected.id,
        (messages) => {
          if (chat && chat.status !== 'submitted' && chat.status !== 'streaming') {
            chat.messages = messages;
          }
        },
        chat?.messages
      );
    } catch (error) {
      useChatStore.getState().handleError(error);
    }
  }, []);
  const clearActiveError = active.clearError;
  const dismissError = useCallback(() => {
    clearActiveError();
    useChatStore.getState().dismissError();
  }, [clearActiveError]);
  const { messages: savedMessages, error: storedError, ...savedState } = saved;
  return {
    ...savedState,
    messages: active.messages,
    savedMessages,
    conversationActivities,
    conversationPreviews,
    queuedMessages,
    dispatchingMessage,
    retryAttempt,
    maxRetries,
    canRetry,
    retryLast,
    query,
    setQuery,
    busy: active.status === 'submitted' || active.status === 'streaming' || retryAttempt > 0,
    status: retryAttempt > 0 && active.status === 'error' ? 'submitted' : active.status,
    error: active.error?.message || storedError,
    sendMessage,
    sendText: (text: string) => sendMessage({ messageText: text }),
    removeQueuedMessage,
    steerQueuedMessage,
    answerQuestion,
    stop,
    stopAll,
    dismissError,
    openConversation,
    loadMoreMessages,
    refreshSelectedMessages,
    setConversationAppId: store.setConversationAppId,
    handleError: store.handleError,
  };
}
